import {
  IconButton,
  Stack,
  Typography,
  Button,
  FormControl,
  InputLabel,
  InputAdornment,
  OutlinedInput,
} from "@mui/material";
import { styled, darken, lighten } from "@mui/material/styles";
import {
  Search,
  SmartToyOutlined,
  Assessment,
  StoreRounded,
  PeopleRounded,
  PollRounded,
  DataUsageRounded,
} from "@mui/icons-material";
import { grey } from "@mui/material/colors";
import { useLocation, useNavigate } from "react-router-dom";
import CheckPermission from "../components/CheckPermission";
import { UserTypeEnum } from "../models/authorize.model";
import clsx from "clsx";
import { useConfig } from "../containers/ConfigContext";
import {
  FilterModel,
  getFilterOptions,
  getOptions,
} from "../models/report.model";
import { useForm } from "react-hook-form";
import FormPeriod from "../components/Form/FormPeriod";
import FormSelection from "../components/Form/FormSelection";
import { titleStr } from "../lib/helper";
import SourceIcon from "@mui/icons-material/Source";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";

const MenuButton = styled(Button)(({ theme }) => ({
  width: "100%",
  justifyContent: "flex-start",
  textTransform: "initial",
  color: grey[600],
  "& .dot": {
    display: "block",
    width: theme.spacing(0.5),
    height: theme.spacing(0.5),
    backgroundColor: grey[600],
    transition: "all 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
  },
  "&.active": {
    "&.main": {
      color: theme.palette.primary.main,
      backgroundColor: lighten(theme.palette.primary.main, 0.9),
      "&::before": {
        top: 0,
        right: 0,
        width: 3,
        bottom: 0,
        content: '""',
        position: "absolute",
        borderTopLeftRadius: 4,
        borderBottomLeftRadius: 4,
        backgroundColor: darken(theme.palette.primary.main, 0.1),
      },
    },
    "&.sub .title": {
      fontWeight: "bold",
      color: "black",
    },
    "& .dot": {
      width: theme.spacing(1),
      height: theme.spacing(1),
      backgroundColor: darken(theme.palette.primary.main, 0.1),
    },
  },
}));

export default function FilterContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { localized } = useConfig();
  const { loggedUser } = useConfig();
  const chartPages = loggedUser?.chartConfig?.pages;
  const hasChartPages = Boolean(chartPages?.length);
  const showComplainsPage = loggedUser?.company?.showComplainsPage;
  return (
    <div className="padding-big padding-t-normal">
      <Stack sx={{ mb: 2 }} direction="column" spacing={1}>
        {hasChartPages && (
          <>
            {chartPages?.map((p) => {
              return (
                <MenuButton
                  onClick={() => navigate(`/charts/${p.url}`)}
                  key={p.url}
                  className={clsx({
                    main: true,
                    active: location.pathname === `/charts/${p.url}`,
                  })}
                >
                  <div className="between-wrapper width-100p">
                    <div className="center-wrapper">
                      <Assessment />
                      <Typography sx={{ ml: 2 }} variant="body2">
                        {localized(titleStr(p.title))}
                      </Typography>
                    </div>
                  </div>
                </MenuButton>
              );
            })}
          </>
        )}
        <MenuButton
          onClick={() => navigate("/text")}
          className={clsx({
            main: true,
            active: location.pathname === "/text",
          })}
        >
          <div className="between-wrapper width-100p">
            <div className="center-wrapper">
              <DataUsageRounded />
              <Typography sx={{ ml: 2 }} variant="body2">
                {localized("Text Analytics")}
              </Typography>
            </div>
          </div>
        </MenuButton>
        {showComplainsPage && (
          <MenuButton
            onClick={() => navigate("/closeloop")}
            className={clsx({
              main: true,
              active: location.pathname === "/closeloop",
            })}
          >
            <div className="between-wrapper width-100p">
              <div className="center-wrapper">
                <SentimentVeryDissatisfiedIcon />
                <Typography sx={{ ml: 2 }} variant="body2">
                  {localized("Close Loop")}
                </Typography>
              </div>
            </div>
          </MenuButton>
        )}
        <CheckPermission roles={[UserTypeEnum.root, UserTypeEnum.admin]}>
          <>
            <MenuButton
              onClick={() => navigate("/stores")}
              className={clsx({
                main: true,
                active: location.pathname === "/stores",
              })}
            >
              <div className="between-wrapper width-100p">
                <div className="center-wrapper">
                  <StoreRounded />
                  <Typography sx={{ ml: 2 }} variant="body2">
                    {localized("Stores")}
                  </Typography>
                </div>
              </div>
            </MenuButton>
            <MenuButton
              onClick={() => navigate("/users")}
              className={clsx({
                main: true,
                active: location.pathname === "/users",
              })}
            >
              <div className="between-wrapper width-100p">
                <div className="center-wrapper">
                  <PeopleRounded />
                  <Typography sx={{ ml: 2 }} variant="body2">
                    {localized("Users")}
                  </Typography>
                </div>
              </div>
            </MenuButton>
            <MenuButton
              onClick={() => navigate("/records")}
              className={clsx({
                main: true,
                active: location.pathname === "/records",
              })}
            >
              <div className="between-wrapper width-100p">
                <div className="center-wrapper">
                  <SourceIcon />
                  <Typography sx={{ ml: 2 }} variant="body2">
                    {localized("Records")}
                  </Typography>
                </div>
              </div>
            </MenuButton>
          </>
        </CheckPermission>
      </Stack>
      <div className="all-center-wrapper between-wrapper">
        <Typography variant="h6" component="span">
          {localized("Experience Bot")}
        </Typography>
        <SmartToyOutlined />
      </div>
      <FormControl fullWidth sx={{ mb: 3, mt: 1 }} variant="outlined">
        <InputLabel>{localized("Search")}</InputLabel>
        <OutlinedInput
          endAdornment={
            <InputAdornment position="end">
              <IconButton edge="end">{<Search />}</IconButton>
            </InputAdornment>
          }
        />
      </FormControl>
      <FilterForm />
    </div>
  );
}

export function FilterForm(props: { onClose?: () => void }) {
  const { onClose } = props;
  const { loggedUser, setFilter, filter, localized } = useConfig();
  const methods = useForm<FilterModel>({ defaultValues: filter });
  const { handleSubmit, control, reset, setError } = methods;

  return (
    <>
      <Stack sx={{ mb: 3 }} direction="row" justifyContent="space-between">
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            handleSubmit((v) => {
              setFilter(v);
              onClose && onClose();
            })();
          }}
        >
          {localized("Apply")}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => {
            reset();
            setFilter(undefined);
            onClose && onClose();
          }}
        >
          {localized("Reset")}
        </Button>
      </Stack>
      <Stack direction="column" spacing={3}>
        <FormControl fullWidth>
          <FormPeriod
            control={control}
            name="period"
            label={localized("Period")}
            setError={setError}
          />
        </FormControl>
        <FormControl fullWidth>
          <FormSelection
            options={getOptions(loggedUser)}
            label={localized("Hierarchy Filters")}
            name="hierarchyFilter"
            control={control}
            setError={setError}
          />
        </FormControl>
        {getFilterOptions(loggedUser).map((a) => (
          <FormControl key={a.name} fullWidth>
            <FormSelection
              options={a.options}
              label={localized(a.label)}
              name={a.name}
              control={control}
              setError={setError}
            />
          </FormControl>
        ))}
      </Stack>
    </>
  );
}
